import type { Finding } from '#/graphql/types';
import type { ColumnProps } from '#/common/components/DataGrid';
import { statusItems, typeItems, priorityItems, Priority } from '../../utils/constants';
import { withChip, withDot } from '#/common/utils/wrappers';
import { color } from '@nuvolo/nuux-theme/utils';
import { CustomChipColors } from '#/common/utils/wrappers/withChip';
import { gql } from '@apollo/client';

// Priority/severity dot colors
export const priorityColorMap: Record<string, string> = {
  1: color('info', 'light'),
  2: color('success', 'light'),
  3: color('reporting', 'green'), // Actually yellow
  4: color('warn', 'light'),
  5: color('error', 'light'),
};

// Define custom color schemes for finding datagrid chips
export const findingStatusColors: Record<string, CustomChipColors> = {
  new: {
    backgroundColor: color('info', 'dark', 0.15),
    textColor: color('info', 'dark'),
  },
  planned: {
    backgroundColor: color('reporting', 'green', 0.15),
    textColor: color('reporting', 'green'), //actually yellow
  },
  in_progress: {
    backgroundColor: color('warn', 'light', 0.15),
    textColor: color('warn', 'light'),
  },
  wo_created: {
    backgroundColor: color('reporting', 'red', 0.15),
    textColor: color('reporting', 'red'),
  },
  in_review: {
    backgroundColor: color('error', 'dark', 0.15),
    textColor: color('error', 'dark'),
  },
  resolved: {
    backgroundColor: color('success', 'dark', 0.15),
    textColor: color('success', 'dark'),
  },
};

const columns: ColumnProps<Finding>[] = [
  { dataField: 'id', caption: 'ID', visible: false, showInColumnChooser: false },
  {
    dataField: 'displayNumber',
    caption: 'Number',
    visible: true,
    openOnClick: true,
    fixed: true,
    dataType: 'string',
  },
  {
    dataField: 'asset.assetTag',
    caption: 'Asset',
    openOnClick: true,
    onClick: (e) => {
      e.navigate('/assets/' + e.row.asset?.id);
    },
    visible: true,
    dataType: 'string',
  },
  {
    dataField: 'assetId',
    caption: 'AssetId',
    visible: false,
    dataType: 'string',
    showInColumnChooser: false,
  },
  {
    dataField: 'shortDescription',
    caption: 'Title',
    visible: true,
    openOnClick: true,
    dataType: 'string',
  },
  {
    dataField: 'type',
    caption: 'Type',
    visible: true,
    dataType: 'string',
    lookup: {
      nullable: true,
      dataSource: typeItems,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  {
    dataField: 'tenant.name',
    caption: 'Organization',
    visible: true,
    allowFiltering: true,
    dataType: 'string',
  },
  {
    dataField: 'status',
    caption: 'Status',
    visible: true,
    dataType: 'string',
    lookup: {
      nullable: true,
      dataSource: statusItems,
      displayExpr: 'label',
      valueExpr: 'key',
    },
    cellRender: (e) => {
      if (!e.value) {
        return null;
      }
      // Use custom colors based on status value
      const customColors = findingStatusColors[e.value as string];
      return withChip(e.displayValue || '', 'normal', customColors);
    },
  },
  {
    dataField: 'priority',
    caption: 'Priority',
    visible: true,
    dataType: 'number',
    lookup: {
      nullable: true,
      dataSource: priorityItems,
      displayExpr: 'label',
      valueExpr: 'key',
    },
    cellRender: (e) => {
      // Check if e.data.priority is a valid Priority enum value
      if (!Object.values(Priority).some((value) => value === e.data.priority)) {
        return null;
      }
      return withDot(e.displayValue || '', priorityColorMap[`${e.value}`] || color('ui', 'dark'));
    },
  },
  {
    dataField: 'reportedOn',
    caption: 'Date Detected',
    visible: true,
    dataType: 'datetime',
  },
  {
    dataField: 'model.name',
    caption: 'Model',
    dataType: 'string',
  },
  {
    dataField: 'updatedAt',
    caption: 'Updated At',
    visible: false,
    dataType: 'date',
    sortIndex: 0,
    sortOrder: 'desc',
    allowSorting: true,
  },
  {
    dataField: 'isArchived',
    caption: 'Archived',
    dataType: 'boolean',
  },
  { dataField: 'thirdPartyId', caption: 'Third Party ID', dataType: 'string', visible: false },
  {
    dataField: 'thirdPartySource',
    caption: 'Third Party Source',
    dataType: 'string',
    visible: false,
  },
];

export default columns;
